import { useId, useState } from 'react';
import type { FieldValue, RenderedValue } from '@whinerp/contracts/fields';
import {
  hiddenFieldValue,
  omissionSummary,
  type OmissionReason,
  type PlannedField,
  type RenderPlan,
} from '@whinerp/contracts/layout';
import { cx } from '../primitives/cx.js';
import { Button, InlineAction } from '../primitives/Button.tsx';
import { Icon } from '../primitives/Icon.tsx';
import { SectionLabel } from '../primitives/Surface.tsx';
import { FIELD_RENDERERS, type FieldRendererLabels } from './field-renderers.tsx';

/**
 * The one component that renders a record's fields.
 *
 * Exported as `LayoutRenderer` - the name the architecture rules use, since it
 * is the one component that renders a layout anywhere. It marks itself
 * `DetailsPanel`, which is what 06_Component_Library calls the thing on a
 * record page. Two documents, two names, one component: the export name is
 * what the import rules police and the marker is what the screen check counts,
 * so each is the name in the place that uses it. What there is not, and must
 * never be, is two components.
 *
 * It renders a plan, and does not compute one. Everything about which fields
 * appear, in which order, and what the reader is told about the ones that do
 * not, is decided by `planLayout` in contracts - a pure function with no React
 * anywhere near it. This file is the translation from plan to elements, and
 * that is all it is, so the interesting half can be tested without a DOM.
 */

export interface FieldConflict {
  field: string;
  /** What this person typed. */
  yours: FieldValue;
  /** What is in the record now, put there by somebody else. */
  theirs: FieldValue;
  /** How the other value renders, so the choice is readable rather than raw. */
  theirsRendered: RenderedValue;
  /** Who changed it, and when, as a sentence the server wrote. */
  by: string;
}

export interface LayoutRendererLabels extends FieldRendererLabels {
  /** The mode switch. */
  simpleMode: string;
  advancedMode: string;
  showAllFields: (count: number) => string;
  /** One sentence per omission reason. Never one lumped total. */
  omission: Record<OmissionReason, (count: number) => string>;
  /** Inline editing. */
  edit: string;
  save: string;
  cancel: string;
  /** Conflict resolution. */
  conflictTitle: string;
  conflictBody: string;
  keepYours: string;
  keepTheirs: string;
  conflictResolved: string;
  savedFieldsCount: (count: number) => string;
  /** The muted line naming the rule beneath its message (F-091). */
  ruleName: (rule: string) => string;
  /** Heads the findings about fields this page does not show. */
  findingsElsewhere: string;
}

/**
 * What a validation rule or a required field says about one field (F-091).
 *
 * Drawn against the field it names, with the rule's name beneath the message,
 * because the UX pointer is exact about it: "always name the rule. An admin
 * cannot fix what they cannot identify."
 */
export interface FieldFinding {
  field: string;
  message: string;
  rule: string;
  severity: 'warn' | 'block';
}

export interface LayoutRendererProps {
  plan: RenderPlan;
  labels: LayoutRendererLabels;
  /** Absent means the record is read-only and no edit affordance is drawn. */
  onSave?: ((changes: Record<string, FieldValue>) => Promise<FieldConflict[]>) | undefined;
  onModeChange?: ((mode: 'simple' | 'advanced') => void) | undefined;
  /** Rule findings, drawn on their fields; ones about fields not shown are listed above. */
  findings?: readonly FieldFinding[] | undefined;
}

export function LayoutRenderer({
  plan,
  labels,
  onSave,
  onModeChange,
  findings = [],
}: LayoutRendererProps): JSX.Element {
  const [edits, setEdits] = useState<Record<string, FieldValue>>({});
  const [conflicts, setConflicts] = useState<FieldConflict[]>([]);
  const [saving, setSaving] = useState(false);
  const editing = Object.keys(edits).length > 0 || conflicts.length > 0;
  const summary = omissionSummary(plan);
  /*
   * A finding about a field this page does not show is still a finding.
   * Dropping it would leave somebody refused with nothing on screen saying
   * why - so those are listed above the sections, still naming the rule.
   */
  const placed = new Set(plan.sections.flatMap((section) => section.fields.map((field) => field.key)));
  const elsewhere = findings.filter((finding) => !placed.has(finding.field));

  const conflictFor = (key: string): FieldConflict | undefined =>
    conflicts.find((c) => c.field === key);

  async function save(): Promise<void> {
    if (!onSave) return;
    let failed = false;
    setSaving(true);
    try {
      /*
       * A rejection here means the save did not happen and is not going to.
       *
       * The edits stay exactly where they are, because the alternative is a
       * form that empties itself on a failure and leaves somebody retyping
       * from memory. Nothing is reported from this primitive: whoever passed
       * `onSave` made the request and is the only one who knows what went
       * wrong with it.
       *
       * A write that was kept for later is not a rejection. It resolves, with
       * no conflicts, because the work is durable - and the fields clear, so
       * nobody saves it a second time into a queue that already has it.
       */
      const remaining = await onSave(edits).catch((error: unknown) => {
        failed = true;
        void error;
        return [];
      });
      if (failed) return;
      setConflicts(remaining);
      if (remaining.length === 0) {
        setEdits({});
      } else {
        // The fields that saved are dropped from the pending edits; only the
        // ones in conflict stay open. A save that rolled everything back
        // because one field collided would throw away work nobody disputed.
        const disputed = new Set(remaining.map((c) => c.field));
        setEdits((current) =>
          Object.fromEntries(Object.entries(current).filter(([key]) => disputed.has(key))),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  function resolve(field: string, keep: 'yours' | 'theirs'): void {
    const conflict = conflictFor(field);
    if (!conflict) return;
    setConflicts((current) => current.filter((c) => c.field !== field));
    setEdits((current) => ({
      ...current,
      [field]: keep === 'yours' ? conflict.yours : conflict.theirs,
    }));
  }

  return (
    <div data-component="DetailsPanel" className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        {onModeChange ? (
          <InlineAction
            data-mode-toggle={plan.mode}
            onClick={() => onModeChange(plan.mode === 'simple' ? 'advanced' : 'simple')}
          >
            {plan.mode === 'simple' ? labels.advancedMode : labels.simpleMode}
          </InlineAction>
        ) : null}

        {onSave && editing ? (
          <span className="ms-auto flex items-center gap-2">
            <Button intent="ghost" onClick={() => { setEdits({}); setConflicts([]); }}>
              {labels.cancel}
            </Button>
            <Button
              intent="primary"
              data-save-record
              onClick={() => void save()}
              disabled={saving || conflicts.length > 0}
            >
              {labels.save}
            </Button>
          </span>
        ) : null}
      </div>

      {conflicts.length > 0 ? (
        <p
          role="alert"
          data-conflict-banner
          className="flex items-start gap-2 rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text"
        >
          <Icon name="Warning" size="inline" decorative />
          <span>
            <strong className="font-medium">{labels.conflictTitle}</strong> {labels.conflictBody}
          </span>
        </p>
      ) : null}

      {elsewhere.length > 0 ? (
        <div data-findings-elsewhere className="flex flex-col gap-1">
          <SectionLabel>{labels.findingsElsewhere}</SectionLabel>
          {elsewhere.map((finding) => (
            <FindingLine key={`${finding.rule}:${finding.field}`} finding={finding} labels={labels} />
          ))}
        </div>
      ) : null}

      {plan.sections.map((section) => (
        <section key={section.key} data-section={section.key} className="flex flex-col gap-2">
          <SectionLabel>{section.label}</SectionLabel>
          <dl
            className={cx(
              'grid gap-x-6 gap-y-3',
              section.columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
            )}
          >
            {section.fields.map((field) => (
              <FieldRow
                key={field.key}
                field={field}
                labels={labels}
                conflict={conflictFor(field.key)}
                findings={findings.filter((finding) => finding.field === field.key)}
                pending={field.key in edits ? edits[field.key] : undefined}
                editable={Boolean(onSave) && field.editable}
                onChange={(next) => setEdits((current) => ({ ...current, [field.key]: next }))}
                onResolve={(keep) => resolve(field.key, keep)}
              />
            ))}

            {/* A field hidden by field-level security keeps its label and says
                what kind of nothing it is. Dropping it entirely would say the
                field does not exist, which is a different fact (P-5). */}
            {section.omitted
              .filter((entry) => entry.reason === 'field-permission')
              .map((entry) => (
                <div key={entry.key} data-hidden-field={entry.key} className="flex flex-col gap-0.5">
                  <dt className="text-micro uppercase tracking-label text-ink-label">{entry.label}</dt>
                  <dd>
                    <HiddenValue />
                  </dd>
                </div>
              ))}
          </dl>
        </section>
      ))}

      {/* Each reason gets its own sentence. One lumped "4 fields hidden" would
          merge a permission decision with a display preference. */}
      {plan.omitted.length > 0 ? (
        <p data-omissions className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-meta">
          {(Object.keys(summary) as OmissionReason[])
            .filter((reason) => summary[reason] > 0 && reason !== 'field-permission')
            .map((reason) => (
              <span key={reason} data-omission={reason}>
                {labels.omission[reason](summary[reason])}
              </span>
            ))}
          {plan.moreInAdvanced && onModeChange && plan.mode === 'simple' ? (
            <InlineAction data-show-all onClick={() => onModeChange('advanced')}>
              {labels.showAllFields(plan.budget.overBudget + summary['advanced-only'])}
            </InlineAction>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

function HiddenValue(): JSX.Element {
  const rendered = hiddenFieldValue();
  if (rendered.kind !== 'absent') return <></>;
  return (
    <span data-absent="hidden" className="inline-flex items-center gap-1 text-body italic text-ink-400">
      <Icon name="EyeSlash" size="inline" decorative />
      {rendered.absent.text}
    </span>
  );
}

function FieldRow({
  field,
  labels,
  conflict,
  findings,
  pending,
  editable,
  onChange,
  onResolve,
}: {
  field: PlannedField;
  labels: LayoutRendererLabels;
  conflict: FieldConflict | undefined;
  findings: readonly FieldFinding[];
  pending: FieldValue | undefined;
  editable: boolean;
  onChange: (next: FieldValue) => void;
  onResolve: (keep: 'yours' | 'theirs') => void;
}): JSX.Element {
  const renderer = FIELD_RENDERERS[field.type];
  const id = useId();
  const conflictId = `${id}-conflict`;
  const findingsId = `${id}-findings`;
  const blocked = findings.some((finding) => finding.severity === 'block');
  const [open, setOpen] = useState(false);
  const isEditing = open || pending !== undefined || conflict !== undefined;

  return (
    <div
      data-field={field.key}
      data-field-type={field.type}
      data-conflict={conflict ? 'true' : undefined}
      className={cx('flex flex-col gap-0.5', conflict && 'rounded-card bg-warning-tint px-2 py-1')}
    >
      <dt className="flex items-baseline gap-1 text-micro uppercase tracking-label text-ink-label">
        <label htmlFor={isEditing ? id : undefined}>{field.label}</label>
        {field.required ? (
          <span aria-hidden className="text-danger">
            *
          </span>
        ) : null}
      </dt>
      <dd className="min-w-0">
        {isEditing ? (
          <renderer.Edit
            id={id}
            field={field.definition}
            value={pending !== undefined ? pending : field.value}
            labels={labels}
            invalid={Boolean(conflict) || blocked}
            describedBy={conflict ? conflictId : findings.length > 0 ? findingsId : undefined}
            onChange={onChange}
          />
        ) : (
          <span className="flex items-center gap-2">
            <renderer.Display field={field.definition} rendered={field.rendered} labels={labels} />
            {editable ? (
              <button
                type="button"
                data-edit-field={field.key}
                aria-label={`${labels.edit}: ${field.label}`}
                onClick={() => setOpen(true)}
                className="opacity-0 transition-opacity duration-state focus-visible:opacity-100 group-hover:opacity-100 hover:opacity-100"
              >
                <Icon name="Pencil" size="inline" label={labels.edit} />
              </button>
            ) : null}
          </span>
        )}

        {findings.length > 0 ? (
          <div id={findingsId} className="mt-1 flex flex-col gap-1">
            {findings.map((finding) => (
              <FindingLine key={finding.rule} finding={finding} labels={labels} />
            ))}
          </div>
        ) : null}

        {/* The conflict lives on the field it belongs to. Its siblings are
            untouched and stay saved: one collision must not throw away work
            nobody disputed. */}
        {conflict ? (
          <div id={conflictId} className="mt-1 flex flex-wrap items-center gap-2 text-meta">
            <span className="text-warning-text">{conflict.by}</span>
            <button
              type="button"
              data-keep="theirs"
              onClick={() => onResolve('theirs')}
              className="rounded-control border border-rule px-2 py-0.5 text-ink hover:bg-sunken"
            >
              {labels.keepTheirs}: <TheirValue conflict={conflict} labels={labels} />
            </button>
            <button
              type="button"
              data-keep="yours"
              onClick={() => onResolve('yours')}
              className="rounded-control border border-rule px-2 py-0.5 text-ink hover:bg-sunken"
            >
              {labels.keepYours}
            </button>
          </div>
        ) : null}
      </dd>
    </div>
  );
}

function FindingLine({
  finding,
  labels,
}: {
  finding: FieldFinding;
  labels: LayoutRendererLabels;
}): JSX.Element {
  return (
    <p
      data-finding={finding.severity}
      data-finding-field={finding.field}
      role={finding.severity === 'block' ? 'alert' : undefined}
      className="flex flex-col"
    >
      <span
        className={cx(
          'flex items-start gap-1 text-dense',
          finding.severity === 'block' ? 'text-danger-text' : 'text-warning-text',
        )}
      >
        <Icon name="Warning" size="inline" decorative />
        <span data-finding-message>{finding.message}</span>
      </span>
      {/* The rule's name, muted, beneath the message - always. */}
      <span data-finding-rule className="ps-5 text-meta text-ink-meta">
        {labels.ruleName(finding.rule)}
      </span>
    </p>
  );
}

function TheirValue({
  conflict,
  labels,
}: {
  conflict: FieldConflict;
  labels: LayoutRendererLabels;
}): JSX.Element {
  const rendered = conflict.theirsRendered;
  if (rendered.kind === 'text') return <>{rendered.text}</>;
  if (rendered.kind === 'boolean') return <>{rendered.value ? labels.yes : labels.no}</>;
  if (rendered.kind === 'lookup') return <>{rendered.value.name}</>;
  if (rendered.kind === 'options') return <>{rendered.values.map((o) => o.label).join(', ')}</>;
  if (rendered.kind === 'absent') return <>{rendered.absent.text}</>;
  return <></>;
}
